<script setup>
import { ref, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxChart, DxSeries, DxLegend, DxLabel, DxValueAxis, DxTick } from 'devextreme-vue/chart'

import ChipStatus from '@/components/shared/ChipStatus.vue'

import { usePortalStore } from '@/stores/portal.store'
import { useSaleStore } from '@/stores/sale.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import RecapPanel from '@/components/shared/RecapPanel.vue'

const props = defineProps({
  gridRef: {
    type: Object,
    default: null
  },
  applyFilter: {
    type: Function,
    default: () => {}
  },
})

const i18n = getI18n()

const portalStore = usePortalStore()
const saleStore = useSaleStore()
const webSiteStore = useWebSiteStore()

const dataSource = ref([])
const recapPanelRef = ref(null)

const creditPanelData = ref({
  value: null,
  updated_at: null,
  expiration_date: null,
})

const loadCreditsAsync = async(force = false) => {
  const res = await saleStore.getOrFetchElectronicInvoiceCreditsAsync(force)
  creditPanelData.value = res
  recapPanelRef.value.formRef.instance.option('formData', { ...creditPanelData.value })
  recapPanelRef.value.formRef.instance.repaint()
}

const loadChartAsync = async() => {
  const baseFamilies = [portalStore.electronicInvoiceFamiliesEnum.DELIVERED, portalStore.electronicInvoiceFamiliesEnum.LOCKED]
  const filterFamily = webSiteStore.user?.has_electronic_invoice_transmission ? [...baseFamilies, portalStore.electronicInvoiceFamiliesEnum.GENERATED] : baseFamilies
  const families = portalStore.getLocalizedElectronicInvoiceFamilies().filter(f => !filterFamily.includes(f.id))
  const details_res = (await saleStore.api.getElectronicInvoiceChartDataAsync())?.data

  dataSource.value = families.map(f => ({
    family: f.nameAlt,
    family_id: f.id,
    count: details_res.find(b => b.family === f.id)?.count ?? 0,
    color: f.color,
  }))
}

const refreshAsync = async() => {
  try {
    await loadChartAsync()
  } finally {
    await loadCreditsAsync(true)
  }
}

onMounted(async() => {
  await loadChartAsync()
  await loadCreditsAsync()
})

const customizePoint = (point) => {
  const item = dataSource.value.find(d => d.family === point.argument)
  return item ? { color: item.color } : {}
}

const onPointClick = (e) => props.applyFilter([e.target.data.family_id], 'progress_status_family_code', props.gridRef, null)

const creditPanelFields = [
  {
    dataField: 'value',
    label: { text: i18n.t('common.credit', 2) },
    template: 'electronicInvoiceCreditTemplate',
  },
  {
    dataField: 'updated_at',
    label: { text: i18n.t('common.updatedAt') },
    editorType: 'dxDateBox',
    editorOptions: { type: 'datetime' },
  },
  {
    dataField: 'expiration_date',
    label: { text: i18n.t('common.expirationDate') },
    editorType: 'dxDateBox',
    editorOptions: { type: 'datetime' },
  },
]

defineExpose({ refreshAsync })
</script>

<template>
  <div style="position: relative">
    <div style="position: absolute; right: 0">
      <q-btn
        dense
        flat
        round
        color="primary"
        icon="mdi-refresh"
        @click="refreshAsync"
      />
    </div>
    <div style="margin-top: 3rem;">
      <div class="row justify-between">
        <div class="col-9">
          <dx-chart
            id="chart"
            :data-source="dataSource"
            :rotated="true"
            :customize-point="customizePoint"
            @point-click="onPointClick"
          >
            <dx-value-axis
              :visible="false"
              :show-zero="true"
            >
              <dx-tick :visible="false" />
              <dx-label :visible="false" />
            </dx-value-axis>
            <dx-series
              argument-field="family"
              value-field="count"
              type="bar"
            >
              <dx-label
                :visible="true"
                position="outside"
              />
            </dx-series>
            <dx-legend :visible="false" />
          </dx-chart>
        </div>

        <div class="col-3 q-pa-lg flex flex-col justify-center">
          <div style="width: 13rem">
            <recap-panel
              ref="recapPanelRef"
              :form-data="creditPanelData"
              :form-items="creditPanelFields"
            >
              <template #electronicInvoiceCreditTemplate="{ data }">
                <chip-status :data="{ name: data?.editorOptions?.value || 0, color: data.editorOptions?.value > saleStore.electronicInvoiceCreditOptions.minCreditsBeforeWarning ? saleStore.electronicInvoiceCreditOptions.color : saleStore.electronicInvoiceCreditOptions.warningColor }" />
              </template>
            </recap-panel>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
#chart {
  height: 14rem;
  width: auto;
}
</style>
